import React, { memo, useMemo, useCallback } from 'react';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { JiraProjectAri } from '@atlassian/ari/jira/project';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import type { RealtimeEvent } from '../../common/types/events.tsx';
import { SOURCE_SUBSCRIPTIONS } from '../../common/constants/sources.tsx';
import { SUBSCRIPTION_SUPPORTED_EVENTS } from '../../common/constants/events.tsx';
import { analyticEvents, analyticsComponent } from '../../common/constants/analyticEvents.tsx';
import { IssueSubscription } from './subscriptions/IssueSubscription.tsx';

const getProjectIdsToSubscribe = (channels: string[]) =>
	channels
		?.map((projectAri) => JiraProjectAri.parse(projectAri))
		?.map((data) => data.projectId)
		?.filter(Boolean) ?? [];

const getIssueEventToSubscribe = (events: string[]) =>
	events?.filter(Boolean)?.filter((event) => SUBSCRIPTION_SUPPORTED_EVENTS.has(event)) ?? [];

type Props = {
	appId: string;
	channels: string[];
	events: string[];
	onJoin: (channels: string[]) => void;
	onReceive: (event: RealtimeEvent) => void;
	onLeave: (channels: string[]) => void;
};

export const RealtimeSubscriptions: React.FC<Props> = memo((props) => {
	const { appId, channels, events, onJoin, onReceive, onLeave } = props;

	const atlassianAccountId = useAccountId();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const projectIds = useMemo(() => getProjectIdsToSubscribe(channels), [channels]);
	const issueEvents = useMemo(() => getIssueEventToSubscribe(events), [events]);

	const handleOnJoin = useCallback(() => {
		onJoin(channels);
	}, [onJoin, channels]);

	const handleOnReceive = useCallback(
		(event: RealtimeEvent) => {
			const {
				type,
				payload: { atlassianId },
			} = event;

			fireOperationalAnalytics(
				createAnalyticsEvent({}),
				`${analyticsComponent} ${analyticEvents.EVENT_RECEIVED}`,
				{
					source: SOURCE_SUBSCRIPTIONS,
					eventType: type,
					appId,
					isTriggeredBySameUser: atlassianId === atlassianAccountId,
				},
			);

			onReceive(event);
		},
		[appId, atlassianAccountId, createAnalyticsEvent, onReceive],
	);

	const handleOnLeave = useCallback(() => {
		onLeave(channels);
	}, [channels, onLeave]);

	const handleOnError = useCallback(
		(error: Error) => {
			fireOperationalAnalytics(
				createAnalyticsEvent({}),
				`${analyticsComponent} ${analyticEvents.FAILURE}`,
				{
					source: SOURCE_SUBSCRIPTIONS,
					appId,
					channels,
					events: issueEvents,
					error: error.message,
				},
			);
		},
		[channels, appId, issueEvents, createAnalyticsEvent],
	);

	const shouldRenderIssueSubscription = projectIds?.length > 0 && issueEvents?.length > 0;

	if (shouldRenderIssueSubscription) {
		return (
			<IssueSubscription
				appId={appId}
				projectIds={projectIds}
				events={issueEvents}
				onJoin={handleOnJoin}
				onReceive={handleOnReceive}
				onLeave={handleOnLeave}
				onError={handleOnError}
			/>
		);
	}
	return null;
});
